import Modal from '../../reusable/modal'
import type { AnalysisClient, Outcome } from './BatchAnalysis'
import { BatchRowDetail } from './BatchRowDetail'
import { clientRejectionReasons, money, reasonLabel, rejectionDetailsFor, shortDate } from './analysisReasons'
import { BatchRowRecord } from './shared'
import styles from './runsTable.module.scss'

export type ModalClient = AnalysisClient & { outcome?: Outcome }

const OUTCOME_TEXT: Record<Outcome, string> = {
    eligible: 'Eligible',
    ineligible: 'Not eligible',
    duplicate: 'Duplicate — deducted',
}

const yesNo = (value: unknown) => (value ? 'Yes' : 'No')

const detailText = (details: Record<string, unknown> | null | undefined) =>
    details
        ? Object.entries(details)
              .filter(([, value]) => value !== undefined)
              .map(([key, value]) => {
                  const shown =
                      value === null || value === ''
                          ? 'none'
                          : typeof value === 'object'
                          ? JSON.stringify(value)
                          : String(value)
                  return `${key.replaceAll('_', ' ')}: ${shown}`
              })
              .join(' · ')
        : ''

const splitName = (client: AnalysisClient) => {
    if (client.firstname || client.lastname) {
        return { firstname: client.firstname || null, lastname: client.lastname || null }
    }
    const parts = String(client.name || '').trim().split(/\s+/).filter(Boolean)
    if (!parts.length) return { firstname: null, lastname: null }
    return { firstname: parts.slice(0, -1).join(' ') || parts[0], lastname: parts.length > 1 ? parts[parts.length - 1] : null }
}

/**
 * The eligibility verdict for one client as the analysis saw it: every gate
 * that failed (headline first), the screening evidence, which documents exist
 * and how each agreement valued.
 */
export const EligibilityPanel = ({ client }: { client: ModalClient }) => {
    const outcome: Outcome = client.outcome || (client.clientRejectionReason ? 'ineligible' : 'eligible')
    const reasons = outcome === 'ineligible' ? clientRejectionReasons(client) : []
    const aml = client.aml
    const documents = client.documents || {
        creditReport: false,
        amlReport: Boolean(aml?.reportSaved),
        resultJson: false,
    }
    const verdictClass = [
        styles.eligibilityVerdict,
        outcome === 'eligible' ? styles.eligibilityVerdictOk : '',
        outcome === 'duplicate' ? styles.eligibilityVerdictDuplicate : '',
    ]
        .filter(Boolean)
        .join(' ')

    const screeningFacts: [string, string][] = aml
        ? [
              ['AML report', aml.reportSaved ? 'Saved' : 'Missing'],
              ['Screening recorded', yesNo(aml.screeningRecorded ?? aml.identityResult != null)],
              ['Identity result', aml.identityResult || '—'],
              ['Identity score', aml.identityScore || '—'],
              [
                  'Industry searches',
                  aml.hasPreviousIndustrySearch || (aml.industryMatchCount ?? 0) > 0
                      ? `Yes (${aml.industryMatchCount ?? 0} matches)`
                      : 'None',
              ],
              ['Deceased (GRO)', aml.deceasedGroFlag ? 'Yes' : aml.deceasedGro || 'No'],
              ['Deceased (Halo)', aml.deceasedHaloFlag ? 'Yes' : aml.deceasedHalo || 'No'],
              ['CCJs', String(aml.ccjs ?? client.ccjs ?? 0)],
              ['IVA', yesNo(aml.hasIva)],
              ['PEPs', String(aml.peps ?? 0)],
              ['Sanctions', String(aml.sanctions ?? 0)],
              ['RCAs', String(aml.rcas ?? 0)],
              ['CIFAS', aml.cifas || '—'],
              ['Address verified', aml.addressVerified || '—'],
              ['Electoral roll', aml.electoralRoll || '—'],
          ]
        : []

    return (
        <div className={styles.eligibilityPanel}>
            <div className={verdictClass} role="status" aria-label="Eligibility verdict">
                <strong>{OUTCOME_TEXT[outcome]}</strong>
                {outcome === 'ineligible' && <span>{reasonLabel(client.clientRejectionReason)}</span>}
                <span>{money(client.clientTotalValue)} valued</span>
                <span>
                    {client.eligibleClaimCount}/{client.claimCount} agreements eligible
                </span>
            </div>

            {outcome === 'ineligible' && (
                <section>
                    <h4>Failed checks ({reasons.length || 1})</h4>
                    {reasons.length ? (
                        <ul className={styles.eligibilityReasons} aria-label="Failed checks">
                            {reasons.map((reason, index) => {
                                const details = detailText(rejectionDetailsFor(client, reason))
                                return (
                                    <li key={reason}>
                                        <span
                                            className={`${styles.eligibilityReasonTag} ${
                                                index === 0 ? '' : styles.eligibilityReasonTagMuted
                                            }`}
                                        >
                                            {index === 0 ? 'Counted under' : 'Also failed'}
                                        </span>
                                        <span>{reasonLabel(reason)}</span>
                                        {details && <small>{details}</small>}
                                    </li>
                                )
                            })}
                        </ul>
                    ) : (
                        <p className={styles.rowDetailNoProblem}>
                            The funding engine rejected this client without recording a reason.
                        </p>
                    )}
                </section>
            )}

            {Boolean(client.duplicateConflicts?.length) && (
                <section>
                    <h4>Duplicate matches ({client.duplicateConflicts!.length})</h4>
                    <ul className={styles.eligibilityReasons}>
                        {client.duplicateConflicts!.map((conflict, index) => (
                            <li key={index}>
                                <span className={`${styles.eligibilityReasonTag} ${styles.eligibilityReasonTagMuted}`}>
                                    {String(conflict.source || 'match')}
                                </span>
                                <span>{detailText(conflict) || 'Duplicate match'}</span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <section>
                <h4>Documents</h4>
                <div className={styles.rowDetailFacts}>
                    {(
                        [
                            ['Credit report PDF', documents.creditReport ? 'Saved' : 'Missing'],
                            ['AML report PDF', documents.amlReport ? 'Saved' : 'Missing'],
                            ['Credit search JSON', documents.resultJson ? 'Saved' : 'Missing'],
                        ] as [string, string][]
                    ).map(([label, value]) => (
                        <div key={label}>
                            <span className={styles.detailLabel}>{label}</span>
                            <span className={styles.detailValue}>{value}</span>
                        </div>
                    ))}
                </div>
                {client.entryId ? (
                    <p className={styles.rowDetailNoProblem}>
                        Open the <strong>PDF reports</strong> tab to read the saved documents.
                    </p>
                ) : (
                    <p className={styles.rowDetailNoProblem}>
                        This person is not linked to a batch row, so the documents cannot be opened from here.
                    </p>
                )}
            </section>

            <section>
                <h4>AML screening</h4>
                {screeningFacts.length ? (
                    <div className={styles.rowDetailFacts}>
                        {screeningFacts.map(([label, value]) => (
                            <div key={label}>
                                <span className={styles.detailLabel}>{label}</span>
                                <span className={styles.detailValue}>{value}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className={styles.rowDetailNoProblem}>No AML screening record was found for this client.</p>
                )}
            </section>

            <section>
                <h4>Agreements as valued ({client.claims?.length || 0})</h4>
                {client.claims?.length ? (
                    <div className={styles.tableWrapper}>
                        <table className={`${styles.runsTable} ${styles.detailDataTable}`}>
                            <thead>
                                <tr>
                                    <th>Agreement</th>
                                    <th>Lender</th>
                                    <th>Start</th>
                                    <th>Starting balance</th>
                                    <th>Monthly</th>
                                    <th>Term</th>
                                    <th>Status</th>
                                    <th>Valuation</th>
                                    <th>Outcome</th>
                                </tr>
                            </thead>
                            <tbody>
                                {client.claims.map((claim, index) => (
                                    <tr key={claim.agreementId ?? `${client.clientKey}-${index}`}>
                                        <td>{claim.policyNumber || '—'}</td>
                                        <td>{claim.lenderResolved || claim.lender || '—'}</td>
                                        <td>{shortDate(claim.startDate)}</td>
                                        <td>{money(claim.loanValue)}</td>
                                        <td>{money(claim.monthlyRepayment)}</td>
                                        <td>{claim.lengthOfContract ? `${claim.lengthOfContract} months` : '—'}</td>
                                        <td>{claim.agreementStatus || '—'}</td>
                                        <td>{money(claim.quantum)}</td>
                                        <td>
                                            {claim.duplicateConflict
                                                ? `Duplicate deducted · ${String(claim.duplicateConflict.source || 'existing data')}`
                                                : reasonLabel(claim.claimRejectionReason)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className={styles.rowDetailNoProblem}>No agreements were found for this client.</p>
                )}
            </section>
        </div>
    )
}

interface AnalysisClientModalProps {
    client: ModalClient
    batchId: string
    /** Shown under the client's name, e.g. the batch filename. */
    batchLabel?: string | null
    onClose: () => void
}

/**
 * Opens a client from the eligibility analysis. When the analysis knows which
 * batch row the person came from, this is the full row transcript (data, PDF
 * reports, raw JSON) with the eligibility verdict as the first tab; otherwise
 * just the verdict.
 */
export const AnalysisClientModal = ({ client, batchId, batchLabel, onClose }: AnalysisClientModalProps) => {
    const subtitle = [
        batchLabel,
        client.rowNumber != null ? `row ${client.rowNumber}` : null,
        client.introducerClientId ? `client ${client.introducerClientId}` : null,
    ]
        .filter(Boolean)
        .join(' · ')
    const panel = <EligibilityPanel client={client} />

    if (client.entryId) {
        const { firstname, lastname } = splitName(client)
        const row: BatchRowRecord = {
            id: client.entryId,
            batch_id: batchId,
            client_id: Number(client.introducerClientId) || null,
            row_number: client.rowNumber ?? 0,
            client_reference: null,
            firstname,
            lastname,
            dob: client.dob || null,
            postcode: client.postcode || null,
            email: client.email || null,
            telephone: client.telephone || null,
            status: 'success',
            provider_reference: null,
            error_message: null,
            result_json_key: null,
            credit_report_key: null,
            aml_report_key: null,
            created_at: null,
        }
        return (
            <BatchRowDetail
                row={row}
                onClose={onClose}
                leadTab={{ label: 'Eligibility', content: panel }}
                subtitle={subtitle || undefined}
            />
        )
    }

    return (
        <Modal modalState previewModalAction={onClose}>
            <div className={styles.rowDetail}>
                <div className={styles.rowDetailHeader}>
                    <div>
                        <h3>{client.name || 'Unnamed client'}</h3>
                        <span>{subtitle || client.email || '—'}</span>
                    </div>
                </div>
                {panel}
            </div>
        </Modal>
    )
}
